<template>
  <div>
    <template v-if="surveys.length === 0 && !loading">
      <p>
        {{ $t('pages.CompaniesCustomSurveyAnswers.noSurveys.content1') }}
      </p>
      <p>
        {{ $t('pages.CompaniesCustomSurveyAnswers.noSurveys.content2') }}
      </p>
      <div class="c-button c-button--left">
        <router-link
          class="c-button__item c-button__item--primary"
          :to="{ name: 'CompaniesSurveys', params: {company_uuid: companyUuid} }"
        >
          {{ $t('pages.CompaniesCustomSurveyAnswers.noSurveys.publishSurvey') }}
        </router-link>
      </div>
    </template>
    <div
      v-else
      class="p-box"
    >
      <div v-if="customSurvey" class="surveyForm">
        <span class="surveyForm__label">
          {{ $t('pages.CompaniesCustomSurveyAnswers.selectSurvey') }}
        </span>
        <div class="surveyForm__input" :style="{ width: surveyFormWidth + 'px' }">
          <KkSuggestField
            id="survey"
            :value="inputSurveyName"
            :suggests="selectableSurveys"
            @select-suggest="changeSurvey"
            @close-suggest="handleSuggestFieldClose"
            @focus-field="handleSuggestFieldFocus"
            @input-field="handleInputSurveyName"
          >
            <template #suggest="slotProps">
              <KkSurveySelectOption :survey="slotProps.item" />
            </template>
          </KkSuggestField>
        </div>
      </div>
      <form class="p-box__header">
        <div class="p-filtersA">
          <div class="p-filtersA__left">
            <div class="c-buttonFilter" @click="openModal">
              {{ $t('labels.filters') }}
              <div class="c-buttonFilter__icon">
                <Icon name="iconFilter" />
              </div>
            </div>
          </div>
          <div class="p-filtersA__right">
            <form class="c-searchField" onsubmit="return false;">
              <input
                v-model="keyword"
                :disabled="loading"
                type="text"
                class="searchForm"
                :placeholder="$t('labels.search')"
                @keydown.enter="search"
              >
              <Icon name="iconSearch" />
            </form>
            <div class="c-select c-select--dateRangePicker">
              <DatePicker
                v-if="period"
                :initial-dates="period"
                opens="left"
                @input="changePeriod"
              />
            </div>
            <div v-if="csvManageModes.length > 0" class="c-select">
              <select :value="selectedCsvManageMode" @change="handleChangeCsvManage">
                <option value="" disabled>{{ $t('pages.CompaniesCustomSurveyAnswers.csvManage.title') }}</option>
                <option v-for="(mode, i) in csvManageModes" :key="`csv_${i}`" :value="mode">
                  {{ $t('pages.CompaniesCustomSurveyAnswers.csvManage.' + mode) }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </form>
      <div class="p-box__body">
        <Loading v-show="loading" />
        <template v-if="customSurvey && !loading">
          <template v-if="answers.length">
            <div class="tableInfo">
              <div class="tableInfo__count">
                {{ $t('pages.CompaniesCustomSurveyAnswers.totalCount', { number: numberLocaleString(meta.totalCount) }) }}
              </div>
              <div class="tableInfo__preview">
                <a class="tableInfo__preview__link" @click.prevent="openPreview(customSurvey.id)">
                  {{ $t('pages.CompaniesCustomSurveyAnswers.viewSurveyForm') }}
                </a>
              </div>
            </div>
            <AnswerTable
              :questions="questions"
              :answers="answers"
              :sort-key="sortKey"
              :sort-order="sortOrder"
              :has-email-setting="customSurvey.hasAnswererEmailSetting"
              @sort="sort"
            />
          </template>
          <div v-else v-text="$t('messages.noData')" />
          <div v-if="meta" class="p-controller">
            <div class="p-controller__count">
              <p class="p-controller__count__name">
                {{ $t('labels.perPageCount') }}
              </p>
              <div class="p-controller__count__select">
                <select :value="displayNumber" @change="changeDisplayNumber($event)">
                  <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                </select>
              </div>
              <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
            </div>
            <div class="p-controller__navi">
              <button
                type="button"
                :disabled="loading || currentPage === 1"
                class="p-controller__navi__prev"
                @click="goPrevPage"
              >
                <Icon name="arrowLeft" />
              </button>
              <button
                type="button"
                :disabled="loading || currentPage >= meta.totalPages"
                class="p-controller__navi__next"
                @click="goNextPage"
              >
                <Icon name="arrowRight" />
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 絞り込み条件モーダル -->
    <KkFilterModal
      :is-visible="modal"
      :filter-items="filterItems"
      :form-values="formValues"
      :total-count="filteredCount"
      @close="modal = false"
      @submit="handleFilter"
      @reset="reset"
      @change-check-box="handleChangeFilterModalCheckBox"
    />
    <Modal v-if="isPreview" width="375px" @close="closePreview">
      <Loading v-if="surveyLoading" />
      <SurveyInputFormPreview v-else :survey="survey" />
    </Modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import moment from 'moment'
import { i18n } from '../../../plugins/i18n'
import { toNumberLocaleString } from '../../../modules/localeString'
import Loading from '../../../components/Loading.vue'
import Icon from '../../../components/Icon.vue'
import AnswerTable from '../../../components/CompaniesCustomSurveys/AnswerTable.vue'
import DatePicker from '../../../components/DatePicker'
import { KkFilterModal } from '../../../components/projects'
import { KkSuggestField } from '../../../components/ui'
import { KkSurveySelectOption } from '../../../components/domains/customSurveys'
import Modal from '../../../components/Modal'
import SurveyInputFormPreview from '../../../components/SurveyInputFormPreview'
import { addAttributes } from '../../../modules/filterModalConst'
import { actionEvent } from 'src/biz_spa/modules/googleAnalytics'

export default {
  name: 'CompaniesCustomSurveyAnswers',
  components: {
    Loading,
    Icon,
    AnswerTable,
    KkFilterModal,
    KkSuggestField,
    KkSurveySelectOption,
    Modal,
    DatePicker,
    SurveyInputFormPreview,
  },
  beforeRouteUpdate (to, _, next) {
    next()
    this.handleResetPageAndSubmit()
  },
  data() {
    return {
      customSurvey: null,
      inputSurveyName: '',
      cacheInputSurveyName: '',
      currentPage: 1,
      sortKey: 'answered_at',
      sortOrder: 'desc',
      keyword: this.$route.query['query'] || '',
      surveyChanged: false,
      modal: false,
      isPreview: false,
      selectedCsvManageMode: '',
      filterItems: [],
      surveyFormWidth: 200,
    }
  },
  computed: {
    ...mapState({
      surveysLoading: state => state.companiesCustomSurveySurveys.loading,
      surveyLoading: state => state.companiesCustomSurvey.loading,
      survey: state => state.companiesCustomSurvey.data,
      questions: state => state.companiesCustomSurveyAnswers.questions,
      answers: state => state.companiesCustomSurveyAnswers.answers,
      meta: state => state.companiesCustomSurveyAnswers.meta,
      answersLoading: state => state.companiesCustomSurveyAnswers.loading,
      formValues: state => state.companiesCustomSurveyAnswers.formValues,
      filteredCount: state => state.companiesCustomSurveyAnswers.filteredCount,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      displayNumber: state => state.filters.displayNumber,
      period: state => state.filters.period,
    }),
    loading() { return this.surveysLoading || this.answersLoading || this.searchAttributesLoading },
    ...mapGetters({
      surveys: 'companiesCustomSurveySurveys/analyzableSurveys',
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      canExportAuthority: 'companiesAccessibleApi/canExportAuthority',
      providedFeaturesCsvExport: 'companiesCompany/providedFeaturesCsvExport',
    }),
    csvManageModes() {
      const modes = []
      if (this.canExportAuthority('custom_surveys/answers') && this.providedFeaturesCsvExport) {
        modes.push('download')
      }
      if (this.showOnlyOwnerAuthority) {
        modes.push('upload')
      }
      return modes
    },
    startDataNumber() { return (this.currentPage - 1) * this.displayNumber + 1 },
    endDataNumber() { return this.startDataNumber + this.answers.length - 1 },
    companyUuid () { return this.$route.params.company_uuid },
    urlQueryParams() {
      return {
        query: {
          query: this.keyword || undefined,
          custom_survey_id: this.customSurvey?.id,
        }
      }
    },
    selectableSurveys() {
      return this.surveys.filter(survey => survey.name.includes(this.inputSurveyName))
    },
    readSurveyAnswerParams() {
      const formParams = {
        term: {
          start_date: moment(this.period.startDate).format(i18n.t('format.date.default')),
          end_date: moment(this.period.endDate).format(i18n.t('format.date.default'))
        },
        page: this.currentPage,
        per: this.displayNumber,
        sort_key: this.sortKey,
        sort_order: this.sortOrder,
        ...this.urlQueryParams.query,
      }

      if(this.formValues.stores.length !== 0) formParams['store_uuids'] = this.formValues.stores
      if(this.formValues.brands.length !== 0) formParams['brand_ids'] = this.formValues.brands
      if(this.formValues.franchisees.length !== 0) formParams['franchisee_ids'] = this.formValues.franchisees
      if(this.formValues.areas.length !== 0) formParams['area_ids'] = this.formValues.areas
      if(this.formValues.user_companies.length !== 0) formParams['user_company_ids'] = this.formValues.user_companies
      if(this.formValues.prefectures.length !== 0) formParams['prefectures'] = this.formValues.prefectures
      if(this.formValues.original_tag_group_a.length !== 0) formParams['original_tag_group_a_tag_ids'] = this.formValues.original_tag_group_a
      if(this.formValues.original_tag_group_b.length !== 0) formParams['original_tag_group_b_tag_ids'] = this.formValues.original_tag_group_b
      if(this.formValues.original_tag_group_c.length !== 0) formParams['original_tag_group_c_tag_ids'] = this.formValues.original_tag_group_c
      if(this.formValues.original_tag_group_d.length !== 0) formParams['original_tag_group_d_tag_ids'] = this.formValues.original_tag_group_d
      if(this.formValues.original_tag_group_e.length !== 0) formParams['original_tag_group_e_tag_ids'] = this.formValues.original_tag_group_e


      return {
        values: {
          biz_companies_survey_form: formParams
        },
      }
    },
  },
  async created() {
    await Promise.all([
      this.readSearchAttributes(),
      this.readCustomSurveys()
    ])
    if (this.surveys.length === 0) return

    this.setInitialCustomSurvey()
    this.setFormValues(this.formValuesFromQueryParams())
    this.createFilter()
    this.handleSubmit()
    this.handleClearUnreadAnswersCount()
  },
  methods: {
    ...mapMutations({
      setFormValues: 'companiesCustomSurveyAnswers/setFormValues',
    }),
    ...mapActions({
      readCustomSurveys: 'companiesCustomSurveySurveys/readCustomSurveys',
      readCustomSurvey: 'companiesCustomSurvey/readCustomSurvey',
      readCustomSurveyAnswers: 'companiesCustomSurveyAnswers/readCustomSurveyAnswers',
      readCustomSurveyAnswersForFilter: 'companiesCustomSurveyAnswers/readCustomSurveyAnswersForFilter',
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
      setDisplayNumber: 'filters/setDisplayNumber',
      setPeriod: 'filters/setPeriod',
      exportCsv: 'companiesCustomSurveyAnswers/exportCsv',
      clearUnreadSurveyAnswersCount: 'companiesUserActivity/clearUnreadSurveyAnswersCount',
      clearUnreadAnswersCount: 'companiesCustomSurveySurveys/clearUnreadAnswersCount',
    }),
    handleSuggestFieldClose() {
      if (this.inputSurveyName === '') {
        this.handleInputSurveyName({ value: this.cacheInputSurveyName })
      }
      this.cacheInputSurveyName = ''
    },
    handleSuggestFieldFocus() {
      this.cacheInputSurveyName = this.inputSurveyName
      this.handleInputSurveyName({ value: '' })
    },
    setInitialCustomSurvey() {
      const initCustomSurveyId = Number(this.$route.query['custom_survey_id'])
      this.customSurvey = initCustomSurveyId ? this.surveys.find(s => s.id === initCustomSurveyId) : this.surveys[0]
      this.inputSurveyName = this.customSurvey?.name

      this.setInitSurveyFormWidth()
    },
    /**
     * アンケート選択フォームの初期サイズ設定
     * @return { Void }
     */
    setInitSurveyFormWidth() {
      var width = 200
      this.surveys.forEach(s => {
        if (s.name.length * 15 > width) width = s.name.length * 15
      })

      this.surveyFormWidth = width
    },
    changeSurvey(suggest) {
      if (this.loading) return

      const selectedSurvey = this.surveys.find(s => s.id === suggest.suggest.id)
      this.inputSurveyName = selectedSurvey.name
      if (this.customSurvey.id === selectedSurvey.id) return

      this.customSurvey = selectedSurvey
      this.$router.push({ ...this.urlQueryParams })
      this.handleClearUnreadAnswersCount()
      actionEvent('SelectSurveyAnswers')
    },
    openModal() {
      if (this.loading) return

      this.modal = true
    },
    closeModal() { this.modal = false },
    formInitialValues() {
      return {
        stores: [],
        brands: [],
        franchisees: [],
        areas: [],
        user_companies: [],
        prefectures: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: []
      }
    },
    formValuesFromQueryParams() {
      /**
       * $route.query['xxxxx'] の値は string or array or undefined のいずれか
       * ここで array に変換する
       */
      const {
        stores,
        brands,
        franchisees,
        areas,
        user_companies: userCompanies,
        prefectures,
        original_tag_group_a: originalTagGroupA,
        original_tag_group_b: originalTagGroupB,
        original_tag_group_c: originalTagGroupC,
        original_tag_group_d: originalTagGroupD,
        original_tag_group_e: originalTagGroupE,
      } = this.$route.query

      const storesConvertToArray = []
      if (typeof stores === 'string') {
        storesConvertToArray.push(stores.split(','))
      } else if (Array.isArray(stores)) {
        storesConvertToArray.push(...stores)
      }

      const brandsConvertToArray = []
      if (typeof brands === 'string') {
        brandsConvertToArray.push(brands.split(',').map(Number))
      } else if (Array.isArray(brands)) {
        brandsConvertToArray.push(...brands.map(Number))
      }

      const franchiseesConvertToArray = []
      if (typeof franchisees === 'string') {
        franchiseesConvertToArray.push(franchisees.split(',').map(Number))
      } else if (Array.isArray(franchisees)) {
        franchiseesConvertToArray.push(...franchisees.map(Number))
      }

      const areasConvertToArray = []
      if (typeof areas === 'string') {
        areasConvertToArray.push(areas.split(',').map(Number))
      } else if (Array.isArray(areas)) {
        areasConvertToArray.push(...areas.map(Number))
      }

      const userCompaniesConvertToArray = []
      if (typeof areas === 'string') {
        userCompaniesConvertToArray.push(userCompanies.split(',').map(Number))
      } else if (Array.isArray(userCompanies)) {
        userCompaniesConvertToArray.push(...userCompanies.map(Number))
      }

      const prefecturesConvertToArray = []
      if (typeof areas === 'string') {
        prefecturesConvertToArray.push(prefectures.split(',').map(Number))
      } else if (Array.isArray(prefectures)) {
        prefecturesConvertToArray.push(...prefectures.map(Number))
      }

      const originalTagGroupAConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupAConvertToArray.push(originalTagGroupA.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupA)) {
        originalTagGroupAConvertToArray.push(...originalTagGroupA.map(Number))
      }

      const originalTagGroupBConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupBConvertToArray.push(originalTagGroupB.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupB)) {
        originalTagGroupBConvertToArray.push(...originalTagGroupB.map(Number))
      }

      const originalTagGroupCConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupCConvertToArray.push(originalTagGroupC.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupC)) {
        originalTagGroupCConvertToArray.push(...originalTagGroupC.map(Number))
      }

      const originalTagGroupDConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupDConvertToArray.push(originalTagGroupD.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupD)) {
        originalTagGroupDConvertToArray.push(...originalTagGroupD.map(Number))
      }

      const originalTagGroupEConvertToArray = []
      if (typeof areas === 'string') {
        originalTagGroupEConvertToArray.push(originalTagGroupE.split(',').map(Number))
      } else if (Array.isArray(originalTagGroupE)) {
        originalTagGroupEConvertToArray.push(...originalTagGroupE.map(Number))
      }

      return {
        stores: storesConvertToArray,
        brands: brandsConvertToArray,
        franchisees: franchiseesConvertToArray,
        areas: areasConvertToArray,
        user_companies: userCompaniesConvertToArray,
        prefectures: prefecturesConvertToArray,
        original_tag_group_a: originalTagGroupAConvertToArray,
        original_tag_group_b: originalTagGroupBConvertToArray,
        original_tag_group_c: originalTagGroupCConvertToArray,
        original_tag_group_d: originalTagGroupDConvertToArray,
        original_tag_group_e: originalTagGroupEConvertToArray,
      }
    },
    sort(key) {
      if (this.loading) return

      if (this.sortKey === key) {
        this.sortOrder = (this.sortOrder === 'desc' ) ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortOrder = 'desc'
      }
      this.handleResetPageAndSubmit()
    },
    reset() {
      this.setFormValues(this.formInitialValues())
      this.handleSubmit()
    },
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    /**
     * 表示件数の変更
     * @param  { Object } e
     * @return { Void }
     */
    changeDisplayNumber(e) {
      this.setDisplayNumber(Number(e.target.value))
      this.handleResetPageAndSubmit()
    },
    /**
     * 期間の変更
     * @param  { Object } value
     * @return { Void }
     */
    changePeriod(value) {
      this.setPeriod(value)
      this.handleResetPageAndSubmit()
    },
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.handleSubmit()
    },
    handleFilter() {
      this.handleResetPageAndSubmit()
      actionEvent('SurveyAnswersFilter', JSON.stringify(this.readSurveyAnswerParams))
    },
    handleSubmit() {
      this.readCustomSurveyAnswers(this.readSurveyAnswerParams)
      this.closeModal()
    },
    /**
     * 検索キーワードの変更
     * @param  { Object } event
     * @return { Void }
     */
    search(event) {
      if (event.keyCode !== 13) return
      if (this.keyword === (this.$route.query['query'] || '')) return

      this.$router.push({ ...this.urlQueryParams })
      actionEvent('SearchSurveyAnswers')
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) { return toNumberLocaleString(value) },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)
      this.readCustomSurveyAnswersForFilter(this.readSurveyAnswerParams)
    },
    async handleClearUnreadAnswersCount() {
      const customSurveyId = this.customSurvey?.id
      const result = await this.clearUnreadSurveyAnswersCount({ customSurveyId: customSurveyId })
      if (result) this.clearUnreadAnswersCount(customSurveyId)
    },
    openPreview(id) {
      this.readCustomSurvey(id)
      this.isPreview = true
    },
    closePreview() {
      this.isPreview = false
    },
    async handleChangeCsvManage(e) {
      const mode = e.target.value
      if (mode === 'download') {
        const isSuccess = await this.exportCsv(this.readSurveyAnswerParams)
        if (isSuccess) this.$router.push({ name: 'CompaniesExportResults' })
      } else if (mode === 'upload') {
        this.$router.push({ name: 'CompaniesCustomSurveyAnswerFiles', params: { company_uuid: this.companyUuid, custom_survey_id: this.customSurvey.id } })
      }
    },
    createFilter() {
      const attributes = [
        this.searchAttributes.brands,
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
      ]

      if(this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if(this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if(this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if(this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if(this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      attributes.push(this.searchAttributes.stores)

      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    },
    handleInputSurveyName({ value }) {
      this.inputSurveyName = value
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/_var.scss';
.c-button__item {
  margin: 0 8px;
}

.surveyForm {
  padding: 32px 16px 16px 32px;
  border-bottom: 1px solid #E4E7EB;
  display: flex;
  align-items: center;

  &__label {
    font-weight: bold;
    margin-right: 12px;
  }

  &__input {
    min-width: 200px;
  }
}

.tableInfo {
  display: flex;
  margin-bottom: 16px;

  &__count {
    font-weight: bold;
    font-size: 12px;
  }

  &__preview {
    margin-left: 16px;

    &__link {
      color: $color-accent-primary;
      cursor: pointer;
    }
  }
}

.p-box__header {
  border-bottom: none;
}

.c-searchField input {
  height: 38px;
}
</style>
